import React, { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { Toaster } from 'sonner';

import { queryClient } from '@queries';
import { Text, Stack, u, Heading2 } from '@ui';

import App from './app/app';
import './service-worker';
import { errorStyle } from './main.css';

if (!import.meta.env.DEV) {
    Sentry.init({
        dsn: import.meta.env['VITE_PUBLIC_SENTRY_DSN'],
        integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
        sendDefaultPii: true,
        replaysOnErrorSampleRate: 1.0,
        tracesSampleRate: 0.1,
        tracePropagationTargets: [/^https:\/\/kiosk.elva.golf/],
    });
}

const ErrorFallback = ({
    error,
    resetError,
}: {
    error: unknown;
    componentStack: string;
    eventId: string;
    resetError(): void;
}) => {
    useEffect(() => {
        setTimeout(() => {
            window.location.reload();
        }, 30_000);
    }, []);
    return (
        <div role="alert" className={errorStyle}>
            <Stack gap={u(2)} alignItems="center">
                <Heading2>Something went wrong</Heading2>
                <Text>We're sorry for the inconvenience. We will promptly reload the screen.</Text>
            </Stack>
        </div>
    );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <React.StrictMode>
        <Sentry.ErrorBoundary fallback={ErrorFallback} onReset={() => window.location.reload()}>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
                <Toaster position="bottom-right" richColors closeButton />
            </QueryClientProvider>
        </Sentry.ErrorBoundary>
    </React.StrictMode>,
);
